// =============================================================================
// masterDataExcel.js
// Excel template export / import for ANY md_* table on the Master Data page.
//
// The sibling of utils/customerExcel.js, and deliberately the same shape — same
// flat one-row-per-record layout, same hidden "Lists" sheet driving in-cell
// dropdowns, same { rows, errors } import contract where `rows` is empty if
// ANYTHING failed, so the caller can refuse a partial load.
//
// The difference: customerExcel hard-codes two tables. This one is driven by the
// spec the server already publishes (GET /api/master-data/specs), so every
// table gets the round-trip for free and there is still exactly one copy of the
// column list — see backend MASTER_SPECS.
//
// Column types come from that spec:
//   text   -> free text (upper-cased on the way in when the spec says so)
//   number -> numeric, blank means "not set" rather than zero
//   bool   -> Active/Inactive style dropdown
//   enum   -> dropdown of the spec's `allowed` values
//   ref    -> dropdown of ANOTHER master table's codes, passed in by the page
//             as `refOptions` since only it has loaded them. Written as
//             "CODE — description" so the sheet is readable, and parsed back to
//             the bare code.
// =============================================================================
import ExcelJS from 'exceljs';

const BLANK_ROWS = 200;    // empty rows (carrying dropdowns) appended after the data
const BOOL_OPTS = ['Yes', 'No'];
const NONE = '(None)';     // explicit "leave this enum empty" choice
const DELIM = ' — ';       // separates "CODE — description" in a ref dropdown

// "NET30 — 30 days from invoice" -> "NET30"; a bare "NET30" is left alone. Same
// convention as utils/customerExcel.js, so the two templates read alike.
const codeOf = (raw) => {
  if (raw == null) return '';
  const s = String(raw).trim();
  const i = s.indexOf(DELIM);
  return (i >= 0 ? s.slice(0, i) : s).trim();
};

const triggerDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

// ExcelJS cell.value may be a string, number, or a rich-text/formula object.
const cellText = (cell) => {
  const v = cell?.value;
  if (v == null) return '';
  if (typeof v === 'object') return String(v.text ?? v.result ?? '');
  return String(v);
};

const boolToLabel = (v) => (v ? 'Yes' : 'No');
const labelToBool = (raw) => {
  const s = String(raw).trim().toLowerCase();
  if (!s) return null;
  return ['yes', 'y', 'true', '1', 'active'].includes(s);
};

// Excel column letter for a 0-based index (A, B, … Z, AA, AB …). The naive
// String.fromCharCode(65+i) breaks at 26 columns, and md_stock_codes has 18
// today — close enough to matter the next time a column is added.
const colLetter = (idx) => {
  let n = idx + 1;
  let s = '';
  while (n > 0) {
    const rem = (n - 1) % 26;
    s = String.fromCharCode(65 + rem) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
};

const fileLabel = (spec) => String(spec.label || 'Master_Data').replace(/[^A-Za-z0-9]+/g, '_');
// Excel sheet names cap at 31 chars and reject : \ / ? * [ ]
const sheetName = (spec) => String(spec.label || 'Data').replace(/[:\\/?*[\]]/g, ' ').slice(0, 31);

// Which columns get an in-cell dropdown, and what goes in it.
// `refOptions` is { colId: [{ code, description }] } — only the page has those.
const optionsFor = (col, refOptions = {}) => {
  if (col.type === 'bool') return BOOL_OPTS;
  if (col.type === 'enum') return col.required ? col.allowed : [NONE, ...col.allowed];
  if (col.type === 'ref') {
    const list = (refOptions[col.id] || []).map((o) => (o.description ? `${o.code}${DELIM}${o.description}` : o.code));
    // No options loaded means no validation list — better a plain text column
    // than a dropdown that permits nothing.
    if (list.length === 0) return null;
    return col.required ? list : [NONE, ...list];
  }
  return null;
};

// ---- Export -----------------------------------------------------------------
// `rows` are records straight from the grid load. Note this exports the rows the
// PAGE currently holds — the grid is server-paged, so the dialog warns when a
// filter or a later page means that is not the whole table.
export async function exportMasterTemplate({ spec, rows = [], refOptions = {}, blankRows = BLANK_ROWS }) {
  const wb = new ExcelJS.Workbook();
  wb.creator = 'Mantle';
  wb.created = new Date();

  const ws = wb.addWorksheet(sheetName(spec), { views: [{ state: 'frozen', ySplit: 1 }] });
  const lists = wb.addWorksheet('Lists');
  lists.state = 'veryHidden';

  // One hidden column per dropdown column, so each keeps its own list.
  const listRanges = {};
  let listCol = 0;
  spec.columns.forEach((col) => {
    const opts = optionsFor(col, refOptions);
    if (!opts) return;
    const letter = colLetter(listCol);
    lists.getCell(`${letter}1`).value = col.label;
    opts.forEach((v, i) => { lists.getCell(`${letter}${i + 2}`).value = v; });
    listRanges[col.id] = `=Lists!$${letter}$2:$${letter}$${opts.length + 1}`;
    listCol += 1;
  });

  ws.columns = spec.columns.map((col) => ({
    header: col.label,
    key: col.id,
    width: col.type === 'bool' ? 12 : col.maxLength && col.maxLength > 200 ? 40 : 22,
  }));

  const headerRow = ws.getRow(1);
  headerRow.font = { bold: true, color: { argb: 'FFF8FAFC' } };
  headerRow.alignment = { vertical: 'middle' };
  headerRow.height = 20;
  headerRow.eachCell((cell) => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF000000' } };
  });

  rows.forEach((r, i) => {
    const row = ws.getRow(i + 2);
    spec.columns.forEach((col, ci) => {
      const cell = row.getCell(ci + 1);
      const v = r[col.id];
      if (col.type === 'bool') cell.value = boolToLabel(v);
      else if (col.type === 'enum') cell.value = v || (col.required ? '' : NONE);
      else if (col.type === 'ref') {
        if (!v) cell.value = col.required ? '' : NONE;
        else {
          // Write the same "CODE — description" the dropdown offers, so the
          // exported value matches a list entry and Excel does not flag it.
          const hit = (refOptions[col.id] || []).find((o) => o.code === v);
          cell.value = hit && hit.description ? `${hit.code}${DELIM}${hit.description}` : String(v);
        }
      } else if (col.type === 'number') cell.value = v === null || v === undefined || v === '' ? '' : Number(v);
      else cell.value = v ?? '';
    });
  });

  // Dropdowns, over the data plus the blank rows below it so new records get
  // the same guardrails as edited ones.
  const lastRow = rows.length + blankRows + 1;
  spec.columns.forEach((col, ci) => {
    const formula = listRanges[col.id];
    if (!formula) return;
    for (let r = 2; r <= lastRow; r++) {
      ws.getCell(`${colLetter(ci)}${r}`).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [formula],
        showErrorMessage: true,
        errorStyle: 'warning',
        error: `Pick a ${col.label} from the list.`,
        errorTitle: 'Pick from the list',
      };
    }
  });

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  triggerDownload(blob, `${fileLabel(spec)}_Template.xlsx`);
}

// ---- Import -----------------------------------------------------------------
// Returns { rows, errors }. Rows match the bulk-upsert payload shape.
//
// Columns are matched BY HEADER NAME, not by position, so a file with the
// columns reordered still loads — a spreadsheet that has been through a few
// hands usually has been. An unrecognised header is ignored rather than fatal;
// a missing REQUIRED header is fatal, because every row would fail anyway.
export async function parseMasterTemplate(file, spec, refOptions = {}) {
  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(await file.arrayBuffer());
  const ws = wb.getWorksheet(sheetName(spec)) || wb.worksheets[0];
  if (!ws) return { rows: [], errors: ['Could not find a worksheet in the file.'] };

  const norm = (s) => String(s || '').trim().toLowerCase();
  const byLabel = new Map(spec.columns.map((c) => [norm(c.label), c]));
  const byId = new Map(spec.columns.map((c) => [norm(c.id), c]));

  // header column index (1-based) -> spec column
  const headerMap = new Map();
  ws.getRow(1).eachCell((cell, colNumber) => {
    const h = norm(cellText(cell));
    const col = byLabel.get(h) || byId.get(h);
    if (col) headerMap.set(colNumber, col);
  });

  const found = new Set([...headerMap.values()].map((c) => c.id));
  const missingRequired = spec.columns.filter((c) => c.required && !found.has(c.id));
  if (!found.has(spec.key)) {
    return { rows: [], errors: [`The file has no "${spec.columns.find((c) => c.id === spec.key)?.label || spec.key}" column.`] };
  }
  if (missingRequired.length) {
    return { rows: [], errors: [`The file is missing required column(s): ${missingRequired.map((c) => c.label).join(', ')}.`] };
  }

  const out = [];
  const errors = [];
  const seen = new Set();

  ws.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return; // header

    // Skip entirely blank rows — the template ships 200 of them.
    let anything = false;
    headerMap.forEach((_col, colNumber) => {
      if (String(cellText(row.getCell(colNumber))).trim()) anything = true;
    });
    if (!anything) return;

    const rowErrs = [];
    const rec = {};

    headerMap.forEach((col, colNumber) => {
      const text = String(cellText(row.getCell(colNumber))).trim();

      if (col.type === 'bool') {
        const b = labelToBool(text);
        rec[col.id] = b === null ? (col.default ?? null) : b;
        return;
      }
      if (col.type === 'enum') {
        if (!text || text === NONE) {
          if (col.required) rowErrs.push(`missing ${col.label}`);
          rec[col.id] = null;
          return;
        }
        // Case-insensitive match back onto the canonical spelling — Excel and
        // autocomplete are both happy to change the casing on the way past.
        const hit = col.allowed.find((a) => norm(a) === norm(text));
        if (!hit) rowErrs.push(`"${text}" is not a valid ${col.label}`);
        rec[col.id] = hit || null;
        return;
      }
      if (col.type === 'ref') {
        if (!text || text === NONE) {
          if (col.required) rowErrs.push(`missing ${col.label}`);
          rec[col.id] = null;
          return;
        }
        const code = codeOf(text);
        const known = refOptions[col.id];
        // Only check when the page actually loaded the list. If it did not, let
        // the value through — the server checks it against the real table and
        // the foreign key is the final word either way.
        if (known && known.length) {
          const hit = known.find((o) => norm(o.code) === norm(code));
          if (!hit) rowErrs.push(`"${code}" is not a valid ${col.label}`);
          rec[col.id] = hit ? hit.code : code;
        } else {
          rec[col.id] = col.upper ? code.toUpperCase() : code;
        }
        return;
      }
      if (col.type === 'number') {
        if (!text) { rec[col.id] = null; return; }
        const n = Number(text);
        if (!Number.isFinite(n)) rowErrs.push(`${col.label} "${text}" is not a number`);
        else rec[col.id] = n;
        return;
      }

      if (!text && col.required) rowErrs.push(`missing ${col.label}`);
      rec[col.id] = text ? (col.upper ? text.toUpperCase() : text) : null;
      if (text && col.maxLength && text.length > col.maxLength) {
        rowErrs.push(`${col.label} is longer than ${col.maxLength} characters`);
      }
    });

    const keyVal = rec[spec.key];
    if (keyVal) {
      if (seen.has(keyVal)) rowErrs.push(`duplicate ${spec.key} "${keyVal}" in file`);
      else seen.add(keyVal);
    }

    if (rowErrs.length) { errors.push(`Row ${rowNumber}: ${rowErrs.join(', ')}`); return; }
    out.push(rec);
  });

  if (errors.length) return { rows: [], errors };
  return { rows: out, errors: [] };
}

export default { exportMasterTemplate, parseMasterTemplate };
